<script setup>
// 4-digit PIN keypad for the lock screen and the PIN setup dialog (docs/pwa/DESIGN.md §2).
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { t } from '../i18n';

const props = defineProps({
  length: {
    type: Number,
    default: 4,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // Bumped by the parent on a wrong PIN: shake, red dots, then clear.
  errorKey: {
    type: Number,
    default: 0,
  },
  // 'faceid' | 'key' | null: the button in the bottom-left cell.
  biometric: {
    type: String,
    default: null,
  },
  // i18n params from biometricWords() in webauthn.js.
  biometricWords: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['complete', 'biometric']);

const digits = ref('');
const showError = ref(false);
const pressed = ref('');
const firstKey = ref(null);
let errorTimer = null;
let pressTimer = null;

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

function press(digit) {
  if (props.disabled || showError.value || digits.value.length >= props.length) {
    return;
  }
  digits.value += digit;
  if (digits.value.length === props.length) {
    emit('complete', digits.value);
  }
}

function backspace() {
  if (props.disabled || showError.value) {
    return;
  }
  digits.value = digits.value.slice(0, -1);
}

function clear() {
  digits.value = '';
}

function flash(key) {
  pressed.value = key;
  if (pressTimer !== null) {
    clearTimeout(pressTimer);
  }
  pressTimer = setTimeout(() => {
    pressed.value = '';
    pressTimer = null;
  }, 100);
}

function onKeydown(e) {
  if (e.metaKey || e.ctrlKey || e.altKey) {
    return;
  }
  if (/^[0-9]$/.test(e.key)) {
    e.preventDefault();
    flash(e.key);
    press(e.key);
  } else if (e.key === 'Backspace') {
    e.preventDefault();
    flash('back');
    backspace();
  } else if (e.key === 'Escape') {
    clear();
  }
}

watch(
  () => props.errorKey,
  () => {
    showError.value = true;
    if (typeof navigator.vibrate === 'function') {
      navigator.vibrate(60);
    }
    if (errorTimer !== null) {
      clearTimeout(errorTimer);
    }
    errorTimer = setTimeout(() => {
      showError.value = false;
      digits.value = '';
      errorTimer = null;
    }, 400);
  }
);

// After a successful check the parent may clear the entry (e.g. step 2 → step 3 of the setup).
defineExpose({ clear });

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  // Focus for keyboard users on a PC; on a phone it would only draw a focus ring on "5".
  if (window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
    firstKey.value?.focus({ preventScroll: true });
  }
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
  if (errorTimer !== null) {
    clearTimeout(errorTimer);
  }
  if (pressTimer !== null) {
    clearTimeout(pressTimer);
  }
});

const keyClass = 'flex size-[72px] items-center justify-center rounded-full bg-slate-800 text-[1.75rem] tabular-nums text-slate-100 transition-colors active:bg-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 [@media(hover:hover)]:hover:bg-slate-700';
</script>

<template>
  <div class="flex flex-col items-center">
    <div
      class="flex gap-4"
      :class="showError ? 'pin-shake' : ''"
      aria-hidden="true"
    >
      <span
        v-for="i in length"
        :key="i"
        class="size-3.5 rounded-full border-2 transition-colors"
        :class="showError
          ? 'border-rose-500 bg-rose-500'
          : (i <= digits.length ? 'border-slate-100 bg-slate-100' : 'border-slate-500')"
      />
    </div>
    <p
      class="sr-only"
      aria-live="polite"
    >
      {{ t('lock.entered', { n: digits.length, total: length }) }}
    </p>

    <div
      class="mt-8 grid grid-cols-3 gap-x-6 gap-y-4 transition-opacity"
      :class="disabled ? 'pointer-events-none opacity-60' : ''"
      role="group"
      :aria-label="t('lock.keypad')"
    >
      <button
        v-for="key in KEYS"
        :key="key"
        :ref="key === '5' ? (el) => (firstKey = el) : undefined"
        type="button"
        :class="[keyClass, pressed === key ? 'bg-slate-600' : '']"
        :disabled="disabled"
        @click="press(key)"
      >
        {{ key }}
      </button>

      <button
        v-if="biometric"
        type="button"
        class="flex size-[72px] items-center justify-center rounded-full bg-transparent text-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 [@media(hover:hover)]:hover:text-slate-100"
        :aria-label="t('lock.signInWith', biometricWords)"
        :disabled="disabled"
        @click="emit('biometric')"
      >
        <svg
          class="size-8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M3 8V5a2 2 0 0 1 2-2h3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3" />
          <path d="M9 9v1.5M15 9v1.5M12 9v4h-1M9.5 16a3.5 3.5 0 0 0 5 0" />
        </svg>
      </button>
      <span
        v-else
        aria-hidden="true"
      />

      <button
        type="button"
        :class="[keyClass, pressed === '0' ? 'bg-slate-600' : '']"
        :disabled="disabled"
        @click="press('0')"
      >
        0
      </button>

      <button
        type="button"
        class="flex size-[72px] items-center justify-center rounded-full bg-transparent text-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 [@media(hover:hover)]:hover:text-slate-100"
        :class="[digits.length === 0 ? 'invisible' : '', pressed === 'back' ? 'text-slate-100' : '']"
        :aria-label="t('lock.deleteDigit')"
        :disabled="disabled"
        @click="backspace"
      >
        <svg
          class="size-8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M9 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6-7 6-7Z" />
          <path d="m12 10 4 4M16 10l-4 4" />
        </svg>
      </button>
    </div>
  </div>
</template>
